import {
  QSetStatus,
  useExportQuestionQSetUsageRecordsLazyQuery,
  useGetAllExamCoursesQuery,
} from '@report/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { triggerFileDownload } from '@shared/utils';

import { useQuestionUsageByQSetReportSearchParams } from '../../hooks';

const MAXIMUM_EXPORT_COUNT = 50000;

interface QSetUsageReportExportModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
}

export default function QSetUsageReportExportModal(
  props: Readonly<QSetUsageReportExportModalProps>,
) {
  const { open, onClose, totalCount } = props;
  const { showSnackbar } = useSnackbar();

  const [{ examCourse, qSetStatus }] = useQuestionUsageByQSetReportSearchParams();

  const [exportQuestionQSetUsageRecords, { loading: isExporting }] =
    useExportQuestionQSetUsageRecordsLazyQuery();

  const { data: examCourseData } = useGetAllExamCoursesQuery({
    fetchPolicy: 'cache-first',
  });

  const selectedExamCourse = examCourseData?.reportGetAllExamCourses?.examCourses.find((course) =>
    examCourse?.includes(course.id),
  );
  const handleExport = async () => {
    await exportQuestionQSetUsageRecords({
      variables: {
        exportQuestionQSetUsageRecordsInput: {
          qSetStatuses: qSetStatus ? [qSetStatus] : [],
          examCourseId: examCourse as string,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const signedUrl = data?.reportExportQuestionQSetUsageRecords?.signedUrl;
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const isExceedingMaximumExportCount = totalCount > MAXIMUM_EXPORT_COUNT;
  const disableExport = isExporting || isExceedingMaximumExportCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExporting}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          {isExceedingMaximumExportCount ? (
            <Alert severity='error'>
              จำกัดการดาวน์โหลดสูงสุด {MAXIMUM_EXPORT_COUNT.toLocaleString('th-TH')} รายการต่อครั้ง
            </Alert>
          ) : null}
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Exam Course:
            </Text>
            <Text>{selectedExamCourse?.code}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              สถานะ:
            </Text>
            <Text>{qSetStatus === QSetStatus.Published ? 'Published' : 'Unpublished'}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              รวมทั้งหมด:
            </Text>
            <Text>{totalCount.toLocaleString('th-TH')} รายการ</Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            loading={isExporting}
            disabled={disableExport}
            onClick={handleExport}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
